import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Calendar, MapPin, Tag, ArrowLeft,
    Heart, Share2, Clock, ExternalLink, Ticket
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../api';
import AlertModal from '../components/AlertModal';
import SEO from '../components/SEO';

export default function EventDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, isFavorite, toggleFavorite } = useAuth();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bookingLoading, setBookingLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, title: '', message: '', type: 'info' });

    useEffect(() => {
        loadEvent();
    }, [id]);

    const loadEvent = async () => {
        try {
            const data = await api.getEvent(id);
            setEvent(data);
        } catch (error) {
            console.error('Error loading event:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFavorite = async () => {
        if (!user) {
            setAlert({
                show: true,
                title: 'Login Required',
                message: 'Please login to save favorites',
                type: 'info'
            });
            return;
        }
        await toggleFavorite(event.id);
    };

    const handleBookTickets = async () => {
        if (!event.external_booking_url) return;
        setBookingLoading(true);
        try {
            await api.trackBookingClick(event.id);
        } catch { }
        window.open(event.external_booking_url, '_blank', 'noopener,noreferrer');
        setBookingLoading(false);
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: event.title,
                text: event.description,
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            setAlert({
                show: true,
                title: 'Link Copied!',
                message: 'Event link has been copied to your clipboard',
                type: 'success'
            });
        }
    };

    if (loading) {
        return (
            <main className="main">
                <div className="container" style={{ paddingTop: '40px' }}>
                    <div className="skeleton" style={{ height: '400px', marginBottom: '24px' }} />
                    <div className="skeleton" style={{ height: '200px' }} />
                </div>
            </main>
        );
    }

    if (!event) {
        return (
            <main className="main">
                <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <h2>Event not found</h2>
                    <Link to="/events" className="btn btn-primary" style={{ marginTop: '24px' }}>
                        Browse Events
                    </Link>
                </div>
            </main>
        );
    }

    const favorite = isFavorite(event.id);

    return (
        <main className="main">
            {event && (
                <SEO
                    title={event.title}
                    description={event.description || `Join us for ${event.title} on ${format(new Date(event.date), 'MMMM d, yyyy')} at ${event.location}. ${event.category ? `Category: ${event.category}.` : ''}`}
                    keywords={`${event.title}, ${event.category || 'event'}, Singapore Indian events, ${event.location}, cultural event`}
                    image={event.image_url || '/atam-logo.svg'}
                    type="article"
                />
            )}
            {/* Hero Gradient Section */}
            <div className="event-detail-hero" style={{
                position: 'relative',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(222, 65, 176, 0.9) 0%, rgba(254, 205, 109, 0.9) 50%, rgba(255, 252, 242, 0.9) 100%)'
            }}>
                <div className="container" style={{ position: 'relative', height: '100%' }}>
                    <button
                        onClick={() => navigate(-1)}
                        className="btn btn-secondary event-detail-back-btn"
                        style={{
                            position: 'absolute',
                            top: '24px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(8px)',
                            color: 'white',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        <ArrowLeft size={18} /> Back
                    </button>
                </div>
            </div>

            <div className="container" style={{ marginTop: '32px', position: 'relative', zIndex: 10, paddingBottom: '80px' }}>
                <div className="event-detail-grid">
                    {/* Main Content */}
                    <div>
                        {/* Event Title */}
                        <h1 style={{
                            fontSize: 'clamp(28px, 5vw, 40px)',
                            fontWeight: '700',
                            marginBottom: '24px',
                            color: 'var(--text-primary)'
                        }}>
                            {event.title}
                        </h1>

                        {/* Category and Date/Time Row */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                            alignItems: 'center',
                            marginBottom: '32px'
                        }}>
                            <span className="tag" style={{
                                padding: '8px 16px',
                                fontSize: '14px',
                                background: '#de41b0',
                                border: 'none',
                                color: '#ffffff',
                                fontWeight: '600'
                            }}>
                                {event.category}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                                <Calendar size={20} color="var(--primary)" />
                                {format(new Date(event.date), 'EEEE, MMMM d, yyyy')}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)' }}>
                                <Clock size={20} color="var(--secondary)" />
                                {format(new Date(event.date), 'h:mm a')}
                            </div>
                        </div>

                        {/* Event Image Card */}
                        <div className="card" style={{
                            padding: '0',
                            marginBottom: '24px',
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            background: 'var(--bg-secondary)'
                        }}>
                            <img
                                src={event.image_url || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200'}
                                alt={event.title}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '600px',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                        </div>

                        <div className="card" style={{ padding: '32px', marginBottom: '24px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>About This Event</h3>
                            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                                {event.description}
                            </p>
                        </div>

                        {event.tags && event.tags.length > 0 && (
                            <div className="card" style={{ padding: '24px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                    <Tag size={18} />
                                    <h3 style={{ fontWeight: '600' }}>Tags</h3>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {event.tags.map((tag, i) => (
                                        <span key={i} className="tag">{tag}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div>
                        <div className="card event-detail-sidebar" style={{ padding: '24px', position: 'sticky', top: '96px' }}>
                            {/* Price */}
                            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                                <div style={{
                                    fontSize: '36px',
                                    fontWeight: '700',
                                    color: (event.price && event.price > 0) ? 'var(--text-primary)' : 'var(--success)',
                                    lineHeight: '1.2',
                                    marginBottom: '4px'
                                }}>
                                    {(event.price && event.price > 0) ? `$${event.price}` : 'FREE'}
                                </div>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '4px' }}>per person</div>
                            </div>

                            {/* Book Tickets Button — only if external URL exists */}
                            {event.external_booking_url && (
                                <button
                                    className="btn btn-primary"
                                    style={{
                                        width: '100%',
                                        marginBottom: '12px',
                                        background: 'linear-gradient(135deg, #de41b0, #fecd6d)',
                                        border: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        padding: '14px',
                                        letterSpacing: '0.3px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 10,
                                        boxShadow: '0 4px 16px rgba(222,65,176,0.3)',
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={handleBookTickets}
                                    disabled={bookingLoading}
                                    onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                    onMouseOut={e => e.currentTarget.style.transform = 'none'}
                                >
                                    <Ticket size={20} />
                                    {bookingLoading ? 'Opening...' : 'Book Your Tickets'}
                                    <ExternalLink size={16} />
                                </button>
                            )}

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                                <button className="btn btn-secondary" style={{ width: '100%' }} onClick={handleShare}>
                                    <Share2 size={18} /> Share
                                </button>
                            </div>

                            {/* Event Details */}
                            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '24px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '4px'
                                    }}>
                                        <MapPin size={18} color="var(--primary)" />
                                        <span style={{ fontWeight: '500' }}>Location</span>
                                    </div>
                                    <p style={{ color: 'var(--text-secondary)', paddingLeft: '30px' }}>
                                        {event.location}
                                    </p>
                                </div>

                                {event.capacity && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                            <Ticket size={18} color="var(--accent)" />
                                            <span style={{ fontWeight: '500' }}>Capacity</span>
                                        </div>
                                        <p style={{ color: 'var(--text-secondary)', paddingLeft: '30px', fontSize: '14px' }}>
                                            {event.capacity} spots available
                                        </p>
                                    </div>
                                )}

                                {event.organizer && (
                                    <div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '4px'
                                        }}>
                                            <Calendar size={18} color="var(--secondary)" />
                                            <span style={{ fontWeight: '500' }}>Organizer</span>
                                        </div>
                                        <p style={{ color: 'var(--text-secondary)', paddingLeft: '30px' }}>
                                            {event.organizer}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AlertModal
                isOpen={alert.show}
                onClose={() => setAlert({ ...alert, show: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />
        </main>
    );
}

